import { css } from '@emotion/react';
import { type HTMLAttributes, type ReactNode, useEffect, useRef } from 'react';

import { Container } from '@toolbox/components';
import { makeStyles } from '@toolbox/style';
import { Header } from './Header';
import { TrustIcons } from '../TrustIcons';
import {
  type LeadData,
  type TrustIcon,
  useCloseWidget,
  useFlags,
  useLeadData,
  useWidgetContext,
} from '@widget/core';
import { USPTextRender, USPTextRenderOld } from './USPTextRender';

import type { USPItemProps } from '../USPItem';
import type { ClassesPropOf } from '@toolbox/style';

type USPLayout = 'vertical' | 'horizontal' | null;

export type MainLayoutProps = HTMLAttributes<HTMLDivElement> & {
  classes?: ClassesPropOf<typeof useStyles>;
  header: ReactNode;
  children: ReactNode;
  showFooter?: boolean;
  hideUSP?: boolean | null;
  uspLayout?: USPLayout;
  trustIconsList?: TrustIcon[] | null;
  upsItems?: USPItemProps[] | undefined | null;
  localizedUspLabel?: string | undefined | null;
  swapUspTextAndTrustIcons?: boolean;
  fullWidthLayout?: boolean;
};

const useStyles = makeStyles(({ theme }) => ({
  root: css`
    display: flex;
    flex-direction: column;
    min-height: 100vh;
    min-height: 100dvh;
    background-color: #ffffff;

    ${theme.media.up('md')} {
      background-color: #ffffff;
    }
  `,
  wrapper: css`
    display: flex;
    flex-direction: column;
    justify-content: start;
    align-items: center;
    flex: 1;
    background-color: #ffffff;
    padding-bottom: 60px;
    max-width: 860px;
    width: 100%;
    overflow: hidden;

    ${theme.media.up('sm')} {
      padding-bottom: 10px;
      flex: 0;
    }
  `,
  fullWidthWrapper: css`
    max-width: 100%;
    display: contents;

    ${theme.media.up('sm')} {
      padding-bottom: 0px;
      flex: 0;
    }
  `,
  trustIconContainer: css`
    display: flex;
    justify-content: center;
    align-items: center;
    row-gap: ${theme.spacing(6)};
    column-gap: ${theme.spacing(6)};
    padding-top: ${theme.spacing(6)};
    width: 100%;
  `,
  trustIcon: css`
    cursor: pointer;
  `,
  uspContainer: css`
    display: flex;
    width: 100%;
    justify-content: center;
    padding: 1.5rem 1rem;
    column-gap: ${theme.spacing(8)};
  `,
  uspWrapper: css`
    width: 100%;
    display: flex;
    justify-content: center;
    ${theme.media.up('sm')} {
      padding-top: ${theme.spacing(18)};
      padding-left: ${theme.spacing(8)};
      padding-right: ${theme.spacing(8)};
    }
    ${theme.media.down('sm')} {
      padding: ${theme.spacing(4)};
    }
  `,
  uspContainerVertical: css`
    flex-direction: column;
    align-items: flex-start;
    row-gap: ${theme.spacing(4)};
  `,
  questionContainer: css`
    flex: 1;
    width: 100%;
    min-height: 55vh;
    ${theme.media.down('lg')} {
      min-height: 50vh;
    }
  `,
  questionContainerFullWidth: css`
    min-height: calc(100vh - 100px);
    ${theme.media.down('lg')} {
      min-height: calc(100vh - 74px);
    }
  `,
  questionContainerInternal: css`
    min-height: inherit;
    ${theme.media.up('sm')} {
      padding: ${theme.spacing(8)};
      padding-bottom: ${theme.spacing(4)};
    }
  `,
  questionContainerInternalWithoutPaddings: css`
    display: contents;

    ${theme.media.up('sm')} {
      padding: 0;
    }
  `,
  header: css`
    width: 100%;
    border-bottom: 1px solid ${theme.palette.colors.gray[300]};

    ${Container} {
      display: flex;
      justify-content: space-between;
      width: auto;
      height: 100%;
      margin: auto;
      padding: 0;
    }
  `,
  body: css`
    display: flex;
    flex-direction: column;
    flex: 1;
    background-color: #ffffff;
    justify-content: center;
    align-items: center;
    ${theme.media.down('sm')} {
      padding-top: 4px;
    }
  `,
  bodyFullWidth: css`
    display: contents;
  `,
}));

export const MainLayout = ({
  header,
  children,
  upsItems,
  trustIconsList,
  classes: outerClasses,
  hideUSP,
  uspLayout = 'horizontal',
  fullWidthLayout,
  ...props
}: MainLayoutProps): JSX.Element => {
  const classes = useStyles({ classes: outerClasses });
  const { close, getStepProps, activeId } = useWidgetContext();
  const { setIsOpenModal, isThankYouPageShown } = useCloseWidget();
  const { setLeadData } = useLeadData();
  const { enableExitPopup } = useFlags();
  const stepProps = getStepProps(activeId);

  const wrapperClass = [
    classes.wrapper as any,
    fullWidthLayout ? (classes.fullWidthWrapper as any) : undefined,
  ];
  const questionContainerInternalClass = [
    classes.questionContainerInternal as any,
    fullWidthLayout ? (classes.questionContainerInternalWithoutPaddings as any) : undefined,
  ];
  const questionContainerClass = [
    classes.questionContainer as any,
    fullWidthLayout && (classes.questionContainerFullWidth as any),
  ];

  return (
    // eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions
    <div
      css={classes.root as any}
      {...props}
      tabIndex={0}
      role={'application'}
      onKeyDown={(e) => {
        if (e.key === 'Escape') {
          if (!isThankYouPageShown && enableExitPopup) {
            setIsOpenModal(true);
          } else {
            setLeadData({} as LeadData);
            close();
          }
        }
      }}
    >
      <Header css={classes.header as any} data-element="header">
        <Container maxW="xl">{header}</Container>
      </Header>
      <div css={[classes.body as any, fullWidthLayout && classes.bodyFullWidth]}>
        <div css={wrapperClass}>
          <div css={questionContainerClass}>
            {stepProps?.showUSPList === 'newRegionalTrends' && (
              <USPTextRender
                uspLayout={uspLayout}
                upsItems={upsItems}
                hideUSP={hideUSP}
                classes={classes}
              />
            )}
            <div css={questionContainerInternalClass}>{children}</div>
          </div>
          {!stepProps?.hideFooter && (
            <>
              {stepProps?.showUSPList === 'oldRegionalTrends' && (
                <USPTextRenderOld
                  uspLayout={uspLayout}
                  upsItems={upsItems}
                  hideUSP={hideUSP}
                  classes={classes}
                />
              )}

              <div css={classes.trustIconContainer as any}>
                {trustIconsList && <TrustIcons trustIconsList={trustIconsList} />}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};
